"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import {
  ResultCard,
  ScriptTextArea,
} from "@/components/tools/transliteration-more/shared";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  HEBREW_SYSTEMS,
  type HebrewOptions,
  hebrewLetterRows,
  romanizeHebrew,
  type SpirantMode,
} from "@/lib/tool-kit/transliteration-more/hebrew";

/** English strings. A translated content file overrides these through `ui`. */
export const HEBREW_TRANSLITERATOR_STRINGS = {
  inputLabel: "Hebrew text",
  inputHint:
    "With or without niqqud. Vowel points, dagesh and the shin and sin dots are read when they are there; cantillation marks are ignored.",
  samples: {
    pointed: "בְּרֵאשִׁית בָּרָא אֱלֹהִים",
    unpointed: "שלום עולם",
  },
  samplePointed: "Sample with niqqud",
  sampleUnpointed: "Sample without niqqud",
  optionsLegend: "Options",
  spirantsLabel:
    "General-purpose style: letters without a dagesh (ב כ פ ג ד ת)",
  spirantOptions: {
    plain: "Plain — b, k, p, g, d, t",
    modern: "Modern — v, kh, f",
    classical: "Classical — v, gh, dh, kh, ph, th",
  },
  guttural: "General-purpose style: write aleph and ayin as ’ and ‘",
  capitalize: "Capitalize each word",
  resultsHeading: "Romanized",
  systems: {
    "sbl-academic": "SBL academic style",
    "sbl-general": "SBL general-purpose style",
    "ala-lc": "ALA-LC (Library of Congress)",
  },
  uses: {
    "sbl-academic":
      "Reversible: one symbol per letter and vowel sign. For biblical studies and Semitics.",
    "sbl-general":
      "Phonetic and easy to read. For books and articles for a general audience.",
    "ala-lc":
      "Library catalogs, in the sound-based form of the Library of Congress table.",
  },
  enterText: "Type or paste some Hebrew to romanize.",
  unpointedNotice:
    "one:{count} word has no vowel points|other:{count} words have no vowel points. Its vowels cannot be supplied, so only the consonants are shown (ו, י and ה are written as w, y and h). Add the niqqud for a full result.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "Consonants, side by side",
  letterColumn: "Letter",
  nameColumn: "Name",
  academicColumn: "SBL academic",
  generalColumn: "SBL general",
  alaColumn: "ALA-LC",
  tableNote:
    "Begadkefat letters (ב ג ד כ פ ת) change with a dagesh in the general-purpose style and ALA-LC: bet is v without a dagesh in ALA-LC, kaph is kh, pe is f. The SBL academic style does not mark spirantization.",
} as const;

type Strings = typeof HEBREW_TRANSLITERATOR_STRINGS;

const DEFAULT_OPTIONS: HebrewOptions = {
  spirants: "plain",
  guttural: true,
  capitalize: false,
};

function LetterTable({ strings }: { strings: Strings }) {
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      <DataTable
        head={[
          strings.letterColumn,
          strings.nameColumn,
          strings.academicColumn,
          strings.generalColumn,
          strings.alaColumn,
        ]}
        rows={hebrewLetterRows().map((row) => [
          row.hebrew,
          row.name,
          ...row.values,
        ])}
      />
      <p className={HINT}>{strings.tableNote}</p>
    </section>
  );
}

export default function HebrewTransliteratorWidget() {
  const strings = useToolUi(HEBREW_TRANSLITERATOR_STRINGS);
  const locale = useToolLocale();
  const [text, setText] = useState<string>(strings.samples.pointed);
  const [options, setOptions] = useState<HebrewOptions>(DEFAULT_OPTIONS);

  const results = HEBREW_SYSTEMS.map((system) => ({
    system,
    result: romanizeHebrew(text, system, options),
  }));
  const unpointed = results[0]?.result.unpointed ?? 0;

  return (
    <div className="space-y-6">
      <ScriptTextArea
        dir="rtl"
        hint={strings.inputHint}
        id="he-input"
        label={strings.inputLabel}
        lang="he"
        onChange={setText}
        value={text}
      />
      <div className="flex flex-wrap gap-2">
        <button
          className={BUTTON_SMALL}
          onClick={() => setText(strings.samples.pointed)}
          type="button"
        >
          {strings.samplePointed}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setText(strings.samples.unpointed)}
          type="button"
        >
          {strings.sampleUnpointed}
        </button>
      </div>
      <fieldset className="space-y-3">
        <legend className={LABEL}>{strings.optionsLegend}</legend>
        <div className="max-w-md">
          <SelectField<SpirantMode>
            id="he-spirants"
            label={strings.spirantsLabel}
            onChange={(spirants) => setOptions((o) => ({ ...o, spirants }))}
            options={[
              { value: "plain", label: strings.spirantOptions.plain },
              { value: "modern", label: strings.spirantOptions.modern },
              { value: "classical", label: strings.spirantOptions.classical },
            ]}
            value={options.spirants}
          />
        </div>
        <Checkbox
          checked={options.guttural}
          label={strings.guttural}
          onChange={(guttural) => setOptions((o) => ({ ...o, guttural }))}
        />
        <Checkbox
          checked={options.capitalize}
          label={strings.capitalize}
          onChange={(capitalize) => setOptions((o) => ({ ...o, capitalize }))}
        />
      </fieldset>
      {text.trim() === "" ? (
        <Notice>{strings.enterText}</Notice>
      ) : (
        <section aria-live="polite" className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
          {unpointed > 0 ? (
            <Notice>
              {tPlural(strings.unpointedNotice, locale, unpointed)}
            </Notice>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            {results.map(({ system, result }) => (
              <ResultCard
                copiedLabel={strings.copied}
                copyLabel={strings.copy}
                key={system}
                lang="he-Latn"
                text={result.text}
                title={strings.systems[system]}
                use={strings.uses[system]}
              />
            ))}
          </div>
        </section>
      )}
      <LetterTable strings={strings} />
    </div>
  );
}
